export type ConsentChoice = "pending" | "denied" | "granted" | "custom";
export type OptionalConsentValue = "denied" | "granted";

export interface ConsentCategories {
  advertising: OptionalConsentValue;
  analytics: OptionalConsentValue;
}

export interface ConsentState {
  version: 2;
  policyVersion: string;
  choice: ConsentChoice;
  categories: ConsentCategories;
  updatedAt?: string;
}

type GtagConsentValue = "granted" | "denied";

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

const CONSENT_KEY = "vibwebs-analytics-consent";
// Read once for migration: consent choices already made under the old,
// game-specific key before it moved to this shared one (RuneVault,
// Treasure Grid, and the game-portfolio games all converge on it too).
const LEGACY_CONSENT_KEY = "diamond-rush-consent-v1";
export const CONSENT_POLICY_VERSION = "2026-06-23";
const CONSENT_MAX_AGE_MS = 180 * 24 * 60 * 60 * 1000;

const deniedCategories = (): ConsentCategories => ({
  advertising: "denied",
  analytics: "denied",
});

export class ConsentService {
  private state: ConsentState;
  private listeners = new Set<(state: ConsentState) => void>();

  constructor(private readonly storage: Pick<Storage, "getItem" | "setItem">) {
    this.state = this.load();
  }

  initialize(): ConsentState {
    this.applyConsentMode(this.state.categories);
    return this.getState();
  }

  getState(): ConsentState {
    return {
      ...this.state,
      categories: { ...this.state.categories },
    };
  }

  hasChoice(): boolean {
    return this.state.choice !== "pending";
  }

  acceptOptional(): void {
    this.savePreferences({ advertising: "granted", analytics: "granted" });
  }

  rejectOptional(): void {
    this.savePreferences(deniedCategories());
  }

  savePreferences(categories: ConsentCategories): void {
    const choice: ConsentChoice = categories.advertising === categories.analytics
      ? categories.advertising === "granted" ? "granted" : "denied"
      : "custom";
    this.state = {
      version: 2,
      policyVersion: CONSENT_POLICY_VERSION,
      choice,
      categories: { ...categories },
      updatedAt: new Date().toISOString(),
    };
    this.persistAndApply();
  }

  resetChoice(): void {
    this.state = this.pendingState();
    this.persistAndApply();
  }

  applyNativeConsentStatus(status: "OBTAINED" | "NOT_REQUIRED" | "REQUIRED" | "UNKNOWN"): void {
    if (status === "REQUIRED" || status === "UNKNOWN") {
      this.savePreferences(deniedCategories());
      return;
    }
    this.savePreferences({
      advertising: status === "OBTAINED" ? "granted" : "denied",
      analytics: this.state.categories.analytics,
    });
  }

  subscribe(listener: (state: ConsentState) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private load(): ConsentState {
    try {
      const raw = this.storage.getItem(CONSENT_KEY) ?? this.migrateLegacyRecord();
      const parsed = JSON.parse(raw ?? "{}") as {
        version?: number;
        policyVersion?: string;
        choice?: ConsentChoice;
        categories?: Partial<ConsentCategories>;
        advertising?: string;
        analytics?: string;
        updatedAt?: string;
      };

      if (
        parsed.version === 2
        && parsed.policyVersion === CONSENT_POLICY_VERSION
        && parsed.updatedAt
        && Date.now() - Date.parse(parsed.updatedAt) <= CONSENT_MAX_AGE_MS
        && (parsed.categories?.advertising === "granted" || parsed.categories?.advertising === "denied")
        && (parsed.categories?.analytics === "granted" || parsed.categories?.analytics === "denied")
      ) {
        const categories = parsed.categories as ConsentCategories;
        return {
          version: 2,
          policyVersion: CONSENT_POLICY_VERSION,
          choice: parsed.choice ?? "custom",
          categories,
          updatedAt: parsed.updatedAt,
        };
      }

      // A choice already made in another vibwebs.com game sharing this key,
      // written in the flat {advertising,analytics} shape with no policy
      // version/expiry info of its own (RuneVault, Treasure Grid, the
      // game-portfolio games). Recognize it as already made rather than
      // re-prompting, and adopt Diamond Rush's own policy version/expiry
      // going forward - this doesn't persist anything, same as the other
      // branches here; a real write only happens via savePreferences().
      const flatAdvertising = parsed.categories?.advertising ?? parsed.advertising;
      const flatAnalytics = parsed.categories?.analytics ?? parsed.analytics;
      if (
        (flatAdvertising === "granted" || flatAdvertising === "denied")
        && (flatAnalytics === "granted" || flatAnalytics === "denied")
      ) {
        return {
          version: 2,
          policyVersion: CONSENT_POLICY_VERSION,
          choice: flatAdvertising === flatAnalytics
            ? (flatAdvertising === "granted" ? "granted" : "denied")
            : "custom",
          categories: { advertising: flatAdvertising, analytics: flatAnalytics },
          updatedAt: new Date().toISOString(),
        };
      }

      // Migrate the earlier all-or-nothing record without silently expanding consent.
      if (parsed.choice === "denied") {
        return {
          version: 2,
          policyVersion: CONSENT_POLICY_VERSION,
          choice: "denied",
          categories: deniedCategories(),
          updatedAt: new Date().toISOString(),
        };
      }
    } catch {
      // Invalid consent data safely falls back to pending/denied.
    }
    return this.pendingState();
  }

  // One-time migration: a choice already made under the old,
  // game-specific key before it moved to the shared vibwebs-analytics-consent
  // key. Copies it forward so the choice isn't lost, and so every
  // subsequent read (here and in every other app sharing the key) hits the
  // new key directly.
  private migrateLegacyRecord(): string | null {
    const legacy = this.storage.getItem(LEGACY_CONSENT_KEY);
    if (!legacy) return null;
    this.storage.setItem(CONSENT_KEY, legacy);
    return legacy;
  }

  private pendingState(): ConsentState {
    return {
      version: 2,
      policyVersion: CONSENT_POLICY_VERSION,
      choice: "pending",
      categories: deniedCategories(),
    };
  }

  private persistAndApply(): void {
    this.storage.setItem(CONSENT_KEY, JSON.stringify(this.state));
    this.applyConsentMode(this.state.categories);
    this.notify();
  }

  private applyConsentMode(categories: ConsentCategories): void {
    if (typeof window === "undefined") return;
    window.dataLayer ??= [];
    window.gtag ??= (...args: unknown[]) => { window.dataLayer.push(args); };
    const advertising: GtagConsentValue = categories.advertising;
    window.gtag("consent", "update", {
      ad_storage: advertising,
      analytics_storage: categories.analytics,
      ad_user_data: advertising,
      // Mixed/child audience safety: personalized advertising stays disabled.
      ad_personalization: "denied",
      functionality_storage: "granted",
      security_storage: "granted",
      personalization_storage: "denied",
    });
  }

  private notify(): void {
    this.listeners.forEach((listener) => listener(this.getState()));
  }
}
